import React, { useState } from 'react'
import { Head } from '@inertiajs/react'
import { Check, MessageCircle, Download, ShieldCheck, Stethoscope, CalendarDays, Clock, ArrowRight, Image as ImageIcon } from 'lucide-react'
import FinancePanel from '../components/FinancePanel'

// Phase 7 — PUBLIC patient-facing TREATMENT JOURNEY (/j/:token): several per-visit estimates as one
// sequenced, phased plan. Neutral, pre-approved explainer copy (never AI clinical claims). Accepting
// records agreement ONLY — never invoices. Plain fetch for the accept POST (intake-409 lesson).

const humMins = (m) => {
  m = parseInt(m || 0, 10)
  const h = Math.floor(m / 60), r = m % 60
  const parts = []
  if (h) parts.push(`${h} hour${h === 1 ? '' : 's'}`)
  if (r) parts.push(`${r} min`)
  return parts.join(' ') || `${m} min`
}

const BOOK_REASON = {
  slot_taken: 'that time was just taken', outside_working_hours: 'that time is outside our hours',
  public_holiday: 'that day is a public holiday', too_soon: 'that time is too soon',
  already_booked: 'you already have an appointment then', invalid: "we couldn't read that time",
}

const CHIP = {
  now: { cls: 'bg-amber-50 text-amber-800 border-amber-200', label: 'Best done soon' },
  asap: { cls: 'bg-amber-100 text-amber-900 border-amber-300', label: 'As soon as possible' },
  ready: { cls: 'bg-brand-surface text-brand-primary border-brand-border', label: "When you're ready" },
  optional: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Optional · any time' },
}

// EITHER/OR — the patient has to CHOOSE (Paul 2026-07-14).
// Some teeth present a fork, not a sequence: save the tooth with a root canal and a crown, or take it
// out. Dr Chalita writes both on the chart precisely because the patient must decide, and the decision
// is almost always about money. So we show BOTH, with BOTH prices, and we say plainly that it is a
// choice — never quietly pick one for them.
// THREE (OR MORE) ROADS (Paul 2026-09-25, tooth 15: save it · implant · bridge). The copy was written
// for exactly two ("There are two ways… Both are valid"), and an option that takes the place of work
// already on the plan (a bridge on teeth being crowned anyway) now says what it really ADDS.
const COUNT_WORD = ['', 'one', 'two', 'three', 'four', 'five', 'six']
function Choice({ choice }) {
  const n = choice.options.length
  const count = COUNT_WORD[n] || String(n)
  return (
    <div className="rounded-2xl border-2 border-amber-400 bg-amber-50/60 p-5 shadow-sm">
      <span className="inline-block rounded-full border border-amber-500 bg-amber-500 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
        You choose · {choice.subject}
      </span>
      <h2 className="mt-3 text-[19px] font-bold leading-tight text-brand-ink">
        There are {count} ways to treat {choice.subject} — you decide which
      </h2>
      <p className="mt-1.5 text-[14px] text-brand-ink/80">
        {n === 2
          ? 'Both are valid. One saves the tooth, the other removes it. They cost very different amounts, so Dr Chalita wants you to see both and choose what suits you — she will talk you through it.'
          : `Each is a valid way to treat it, and they cost different amounts, so Dr Chalita wants you to see all ${count} and choose what suits you — she will talk you through it.`}
      </p>
      <div className={`mt-4 grid gap-3 ${n >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        {choice.options.map((o, i) => (
          <div key={o.estimate_id} className="rounded-xl border border-amber-300 bg-white p-4">
            <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700">Option {i + 1}</p>
            <p className="mt-0.5 text-[15px] font-bold text-brand-ink">{o.label}</p>
            <p className="mt-2 text-[20px] font-extrabold tabular-nums text-brand-ink">{o.cost}</p>
            {o.note && <p className="mt-1.5 text-[12.5px] leading-snug text-amber-800">{o.note}</p>}
          </div>
        ))}
      </div>
      <p className="mt-3 text-[13px] font-semibold text-amber-800">
        We will not book this visit until you have told us which one you would like.
      </p>
    </div>
  )
}

function Phase({ phase }) {
  const chip = CHIP[phase.priority] || CHIP.ready
  // A COMPLETED visit stays on the plan, CROSSED OUT (Paul 2026-07-14). The patient sees how far they
  // have come, not just what is left — so the plan can be re-sent as an UPDATE after every visit.
  const done = !!phase.completed
  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${done ? 'border-emerald-200 bg-emerald-50/40' : 'border-brand-border bg-white'}`}>
      {done ? (
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-600 bg-emerald-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          <Check size={12} /> {phase.label} · Done
        </span>
      ) : (
        <span className={`inline-block rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${chip.cls}`}>
          {phase.label} · {chip.label}
        </span>
      )}
      <h2 className="mt-3 text-lg font-semibold text-brand-ink">{phase.title}</h2>
      {phase.choice_note && (
        <p className="mt-1.5 text-[13px] font-semibold text-amber-800">{phase.choice_note}</p>
      )}
      {phase.summary && (
        <p className="mt-2 rounded-lg bg-brand-surface/60 px-3 py-2 text-[14px] leading-snug text-brand-ink/90">
          <span className="font-semibold text-brand-primary">Why this matters for you · </span>{phase.summary}
        </p>
      )}
      {phase.images && phase.images.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {phase.images.map((src, i) => (
            <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="group relative block h-16 w-16 overflow-hidden rounded-lg border border-brand-border">
              <img src={src} alt="Clinical image from your consultation" className="h-full w-full object-cover transition group-hover:scale-105" />
            </a>
          ))}
          <span className="inline-flex items-center gap-1 self-center text-[12px] text-brand-muted"><ImageIcon size={13} /> from your consultation</span>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 border-t border-brand-border pt-3 text-[13px] text-brand-muted">
        <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} /> <b className="font-semibold text-brand-ink">{phase.visits}</b> visit{phase.visits === 1 ? '' : 's'}</span>
        <span className="inline-flex items-center gap-1.5"><Clock size={15} /> about <b className="font-semibold text-brand-ink">{humMins(phase.minutes)}</b></span>
        <span className="inline-flex items-center gap-1.5 font-semibold text-brand-ink">{phase.cost}</span>
      </div>

      <ul className="mt-4 flex flex-col gap-4">
        {phase.treatments.map((t, i) => (
          <li key={i} className="border-l-2 border-brand-surface pl-4">
            <h3 className="text-[15px] font-bold text-brand-ink">
              {t.name}{t.teeth && t.teeth.length ? <span className="font-normal text-brand-muted"> · tooth {t.teeth.join(', ')}</span> : null}
            </h3>
            <ul className="mt-2 flex flex-col gap-1.5">
              {t.bullets.map((b, j) => (
                <li key={j} className="relative pl-4 text-[14px] text-brand-ink/80">
                  <span className="absolute left-0.5 top-2 h-1.5 w-1.5 rounded-full bg-brand-primary/70" />{b}
                </li>
              ))}
            </ul>
            {t.url && (
              <a href={t.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-brand-primary hover:underline">
                Read more <ArrowRight size={13} />
              </a>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        {phase.wa_booking_link && !done && (
          <a href={phase.wa_booking_link} target="_blank" rel="noopener noreferrer"
             className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90">
            <MessageCircle size={16} /> Book this on WhatsApp
          </a>
        )}
        {phase.pdf_url && (
          <a href={phase.pdf_url} target="_blank" rel="noopener noreferrer"
             className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-brand-border bg-white px-4 py-2.5 text-sm font-semibold text-brand-ink transition hover:bg-brand-surface">
            <Download size={16} /> Download PDF
          </a>
        )}
      </div>
    </div>
  )
}

export default function PublicTreatmentJourney({ token, practice = {}, patient = {}, journey = {}, phases = [], finance }) {
  const [accepted, setAccepted] = useState(!!journey.accepted)
  const [signedName, setSignedName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [plan, setPlan] = useState(null)
  const [planning, setPlanning] = useState(false)
  const [booking, setBooking] = useState(false)
  const [bookedN, setBookedN] = useState(null)
  const [bookResults, setBookResults] = useState(null)
  const [fbReason, setFbReason] = useState(null)
  const [fbNote, setFbNote] = useState('')
  const [fbSent, setFbSent] = useState(false)

  const canAccept = signedName.trim().split(/\s+/).length >= 2
  const firstBook = phases[0] && phases[0].wa_booking_link
  const bookFailed = (bookResults || []).filter((r) => !r.ok)

  const postJson = async (path, body) => {
    const csrf = (typeof document !== 'undefined' && document.querySelector('meta[name="csrf-token"]')?.content) || ''
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body || {}), credentials: 'same-origin',
    })
    return { ok: res.ok, data: await res.json().catch(() => ({})) }
  }

  const accept = async () => {
    setError(null)
    if (!canAccept) { setError('Please enter your full name to accept.'); return }
    setSubmitting(true)
    const { ok, data } = await postJson(`/j/${token}/accept`, { signed_name: signedName.trim() })
    if (ok && data.ok) { setAccepted(true); window.scrollTo(0, 0) }
    else { setError(data.error || 'Sorry — we couldn’t record that. Please try again.'); setSubmitting(false) }
  }

  const proposePlan = async () => {
    setPlanning(true)
    const { data } = await postJson(`/j/${token}/propose_schedule`, {})
    if (data.ok) setPlan(data.slots || [])
    setPlanning(false)
  }
  const bookAll = async () => {
    setBooking(true)
    const bookable = (plan || []).filter((s) => s.date && s.time)
    const { data } = await postJson(`/j/${token}/book_all`, { slots: bookable })
    if (data.ok) { setBookedN(data.booked || 0); setBookResults(data.results || []); window.scrollTo(0, 0) }
    setBooking(false)
  }
  // Preview + manually adjust: editing a proposed date/time updates local state; "Confirm & book"
  // then books exactly what is shown (each still validated server-side against the dentist's diary).
  const updateSlot = (phase, field, value) => {
    setPlan((prev) => (prev || []).map((s) => (s.phase === phase ? { ...s, [field]: value } : s)))
  }
  const sendFeedback = async () => {
    if (!fbReason) return
    await postJson(`/j/${token}/feedback`, { choice: 'concern', reason: fbReason, note: fbNote.trim() })
    setFbSent(true)
  }

  const FB = [
    { id: 'cost', t: 'The cost' },
    { id: 'timing', t: 'Timing — too busy' },
    { id: 'think', t: 'Want to think about it' },
    { id: 'understand', t: 'Didn’t fully understand it' },
    { id: 'talk', t: 'Want to talk to someone' },
  ]

  return (
    <div className="min-h-screen bg-brand-surface/40 px-4 py-6">
      <Head title={`${journey.title || 'Treatment plan'} — ${practice.name || 'Dr Chalita le Roux'}`} />
      <div className="mx-auto flex max-w-2xl flex-col gap-5">

        <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-brand-primary">
            <Stethoscope size={18} />
            <span className="text-sm font-semibold">{practice.name || 'Dr Chalita le Roux'}</span>
          </div>
          <h1 className="mt-2 text-xl font-semibold text-brand-ink">Hi {patient.first_name || 'there'}</h1>
          {/* THE PLAN SAYS WHAT IT IS (Paul 2026-07-24): "Don't just say 'treatment plan.' Explain
              exactly, as short and sweet as possible, what we're going to do here. What is this?"
              journey.title was sent to this page from the very first version and used ONLY in the
              browser tab — every heading the patient could actually see was hard-coded, so reception's
              "Plan title (patient sees this)" field changed nothing they saw. */}
          <p className="mt-2 text-[15px] font-semibold text-brand-primary">{journey.title || 'Your treatment plan'}</p>
          <p className="mt-1 text-sm text-brand-muted">
            Here is your plan, set out in simple steps so you can see what we’re doing, how we’ll do it, and the cost of each part. Take your time — there’s no pressure, and you’re welcome to ask us anything.
          </p>
        </div>

        {accepted && (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-800"><Check size={18} /> Thank you — your acceptance has been recorded.</div>
            <p className="mt-1 text-sm text-emerald-800">The next step is to pick a time for your first visit.</p>
            {firstBook && (
              <a href={firstBook} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 sm:w-auto">
                <MessageCircle size={16} /> Book Phase 1 on WhatsApp
              </a>
            )}
          </div>
        )}

        {journey.consult_summary && journey.consult_summary.trim() && (
          <div className="rounded-xl border border-brand-border bg-brand-surface/40 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-muted">What we discussed</p>
            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-brand-ink">{journey.consult_summary}</p>
          </div>
        )}
        <p className="px-1 text-xs font-bold uppercase tracking-wider text-brand-muted">{journey.title || 'Your treatment journey'} · {journey.phase_count || phases.length} phase{(journey.phase_count || phases.length) === 1 ? '' : 's'}</p>
        {phases.map((p, i) => <Phase key={i} phase={p} />)}

        {(journey.choices || []).map((c) => <Choice key={c.key} choice={c} />)}

        <div className="rounded-2xl border border-brand-primary/30 bg-white p-5 shadow-sm">
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-brand-muted">Your full journey</p>
          {phases.map((p, i) => (
            <div key={i} className={`flex items-center justify-between border-b border-brand-border py-2 text-sm ${p.completed ? 'text-brand-muted line-through decoration-emerald-600/70' : 'text-brand-ink/80'}`}>
              <span>{p.label} · {p.title}</span>
              <span className={`font-semibold tabular-nums ${p.completed ? 'text-brand-muted' : 'text-brand-ink'}`}>{p.cost}</span>
            </div>
          ))}
          <div className="mt-1 flex items-center justify-between pt-3 text-sm text-brand-muted">
            <span>Full plan, all phases</span>
            <span className="tabular-nums">
              {journey.choices && journey.choices.length > 0
                ? `${journey.total_low} – ${journey.total_high}`
                : journey.total}
            </span>
          </div>
          {journey.choices && journey.choices.length > 0 && (
            <p className="mt-1 text-[12.5px] text-amber-800">
              The range depends on the choice{journey.choices.length > 1 ? 's' : ''} above — what you decide about {journey.choices.map((c) => c.subject).join(' and ')}.
            </p>
          )}
          {journey.done_count > 0 && (
            <div className="mt-1 flex items-center justify-between text-sm font-semibold text-emerald-700">
              <span>{journey.done_count} done — still to come</span>
              <span className="tabular-nums">{journey.remaining}</span>
            </div>
          )}
          {phases[0] && (
            <div className="mt-3 rounded-xl border border-brand-primary/30 bg-brand-surface/50 p-3">
              <p className="text-[13.5px] text-brand-ink/90">You don’t do this all at once — most people simply start here:</p>
              <div className="mt-1 flex items-baseline justify-between">
                <span className="text-[15px] font-semibold text-brand-ink">Start with {phases[0].label} · {phases[0].title.split('—')[0].trim()}</span>
                <span className="text-lg font-bold tabular-nums text-brand-primary">{phases[0].cost}</span>
              </div>
              <p className="mt-1 text-[13px] text-brand-muted">The rest follows at your pace, planned around your budget and time. We’ll always talk you through the options — including doing less — before anything goes ahead.</p>
            </div>
          )}
          <p className="mt-3 text-[13px] leading-snug text-brand-ink/70">Prefer to spread the cost? We can arrange payment across your visits — just ask, and we’ll set up something that suits you.</p>
        </div>

        {/* Patient finance (Paul 2026-09-30) — the same loan table as the case page, for the whole plan. */}
        {finance && <FinancePanel finance={finance} />}

        {bookedN !== null && bookedN > 0 && (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800">
            <div className="flex items-center gap-2 font-medium"><Check size={18} /> Booked! We’ve reserved {bookedN} appointment{bookedN === 1 ? '' : 's'} for you.</div>
            <p className="mt-1">We’ll ask you to confirm each one closer to the time. Any changes, just reply on WhatsApp.</p>
          </div>
        )}
        {bookedN !== null && bookFailed.length > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-medium">A few visits need a different time:</div>
            <ul className="mt-2 flex flex-col gap-1">
              {bookFailed.map((r) => (
                <li key={r.phase}>• <b>Phase {r.phase}</b> · {r.title} — {BOOK_REASON[r.status] || "that time wasn’t available"}.</li>
              ))}
            </ul>
            {firstBook && (
              <a href={firstBook} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white transition hover:opacity-90">
                <MessageCircle size={16} /> Pick another time on WhatsApp
              </a>
            )}
          </div>
        )}

        {bookedN === null && (
          <div className="rounded-2xl border border-brand-primary/40 bg-white p-5 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-muted">Book your whole plan in one go</p>
            <p className="mt-1 text-sm text-brand-muted">We’ll find the earliest suitable time for each visit, spaced the right amount apart. You confirm — we book them all.</p>
            {!plan && (
              <button onClick={proposePlan} disabled={planning}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50">
                <CalendarDays size={16} /> {planning ? 'Finding times…' : 'Show me the earliest times'}
              </button>
            )}
            {plan && (
              <div className="mt-3">
                <p className="mb-2 text-xs text-brand-muted">Suggested times — you can adjust any date or time before confirming.</p>
                <ul className="flex flex-col gap-2">
                  {plan.map((s) => (
                    <li key={s.phase} className="flex flex-col gap-1.5 rounded-lg border border-brand-border px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-brand-ink"><b className="font-semibold">Phase {s.phase}</b> · {s.title}</span>
                      <span className="flex items-center gap-1.5">
                        <input type="date" value={s.date || ''} onChange={(e) => updateSlot(s.phase, 'date', e.target.value)}
                               className="rounded border border-brand-border px-2 py-1 text-[13px] text-brand-ink" />
                        <input type="time" value={s.time || ''} onChange={(e) => updateSlot(s.phase, 'time', e.target.value)}
                               className="rounded border border-brand-border px-2 py-1 text-[13px] text-brand-ink" />
                      </span>
                    </li>
                  ))}
                </ul>
                <button onClick={bookAll} disabled={booking}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50">
                  <Check size={16} /> {booking ? 'Booking…' : 'Confirm & book these times'}
                </button>
                <p className="mt-2 text-center text-xs text-brand-muted">Prefer different times? {firstBook && <a className="font-semibold text-brand-primary" href={firstBook} target="_blank" rel="noreferrer">Chat to us on WhatsApp</a>}</p>
              </div>
            )}
          </div>
        )}

        {!accepted && bookedN === null && (
          <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
            <p className="mb-1 text-xs font-bold uppercase tracking-wider text-brand-muted">Happy with the plan but not ready to book?</p>
            <p className="mb-3 text-sm text-brand-muted">You can accept it now and arrange the times later.</p>
            <label className="mb-1 block text-xs font-semibold text-brand-muted">Type your full name to accept</label>
            <input value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="Your full name" className="w-full rounded-md border border-brand-border px-3 py-2 text-sm" />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <button onClick={accept} disabled={submitting || !canAccept}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-brand-primary bg-white px-4 py-2.5 text-sm font-semibold text-brand-primary transition hover:bg-brand-surface disabled:cursor-not-allowed disabled:opacity-50">
              <Check size={16} /> {submitting ? 'Recording…' : 'Accept this plan'}
            </button>
          </div>
        )}

        {bookedN === null && (
          <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
            {!fbSent ? (
              <>
                <p className="text-xs font-bold uppercase tracking-wider text-brand-muted">Not sure, or something holding you back?</p>
                <p className="mt-1 text-sm text-brand-muted">Tell us what’s on your mind — no pressure, it just helps us help you.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {FB.map((f) => (
                    <button key={f.id} type="button" onClick={() => setFbReason(f.id)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${fbReason === f.id ? 'border-brand-primary bg-brand-surface text-brand-primary' : 'border-brand-border text-brand-ink hover:bg-brand-surface'}`}>{f.t}</button>
                  ))}
                </div>
                <textarea value={fbNote} onChange={(e) => setFbNote(e.target.value)} rows={2} placeholder="Anything you’d like to add (optional)" className="mt-3 w-full rounded-md border border-brand-border px-3 py-2 text-sm" />
                {fbReason === 'talk' && journey.contact_wa && (
                  <a href={journey.contact_wa} target="_blank" rel="noopener noreferrer" onClick={() => { sendFeedback() }}
                     className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90">
                    <MessageCircle size={16} /> Message us on WhatsApp
                  </a>
                )}
                <button onClick={sendFeedback} disabled={!fbReason}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50">Send this to us</button>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-800"><Check size={18} /> Thank you — we’ve got it, and we’ll be in touch to help.</div>
            )}
          </div>
        )}

        <div className="flex items-start gap-2 rounded-xl border border-brand-border bg-white p-4 text-sm text-brand-muted">
          <ShieldCheck size={18} className="mt-0.5 shrink-0 text-brand-primary" />
          <p>Please note we do <strong className="text-brand-ink">not</strong> claim from medical aid — you pay the practice and claim it back from your medical aid yourself. We’ll give you everything you need for your claim.</p>
        </div>

        <div className="space-y-1 px-2 pb-6 pt-1 text-center text-xs text-brand-muted">
          <p className="font-medium text-brand-ink/80">{practice.name || 'Dr Chalita le Roux'}</p>
          {practice.address && <p>{practice.address}</p>}
          <p>{[practice.hpcsa && `HPCSA: ${practice.hpcsa}`, practice.practice_no && `Practice no: ${practice.practice_no}`, practice.vat && `VAT: ${practice.vat}`].filter(Boolean).join(' · ')}</p>
          {(practice.phone || practice.email) && <p>{[practice.phone, practice.email].filter(Boolean).join(' · ')}</p>}
          <p className="pt-1">This is an estimate of planned treatment, not a final account — actual fees may vary with what’s clinically needed on the day. Valid for 14 days.</p>
        </div>
      </div>
    </div>
  )
}
